import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { MzicSvgComponent } from '@mzic/mzic-components';
import { SpinnerComponent } from '@mzic/mzic-components';
import { CollectionWorkflowTargetDto } from '../../interfaces/collection-revision.interface';

@Component({
  selector: 'lib-target-selection-dialog',
  imports: [CommonModule, TranslocoModule, MzicSvgComponent, SpinnerComponent],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './target-selection-dialog.component.html',
  styleUrl: './target-selection-dialog.component.scss',
})
export class TargetSelectionDialogComponent {
  private readonly translocoService = inject(TranslocoService);

  @Input() targets: readonly CollectionWorkflowTargetDto[] = [];
  @Input() integrationEnabled = false;
  @Input() integrationEntityName = '';
  @Input() loading = false;
  @Output() confirm = new EventEmitter<string[]>();
  @Output() cancel = new EventEmitter<void>();

  selectedTargetIds = signal<Set<string>>(new Set());

  get uploadTargets(): readonly CollectionWorkflowTargetDto[] {
    return this.targets
      .filter((target) => target.mean_type_snapshot === 'UPLOAD')
      .sort(
        (left, right) =>
          left.order_idx_snapshot - right.order_idx_snapshot,
      );
  }

  get canConfirm(): boolean {
    return this.selectedTargetIds().size > 0;
  }

  get integrationSuffix(): string {
    return this.integrationEnabled ? '_integration' : '';
  }

  get shareSelectTargetsSubtitleKey(): string {
    return `lang.share_select_targets_subtitle${this.integrationSuffix}`;
  }

  get shareTargetUploadKey(): string {
    return `lang.share_target_upload${this.integrationSuffix}`;
  }

  isSelected(targetId: string): boolean {
    return this.selectedTargetIds().has(targetId);
  }

  toggleTarget(targetId: string): void {
    const current = new Set(this.selectedTargetIds());
    if (current.has(targetId)) {
      current.delete(targetId);
    } else {
      current.add(targetId);
    }
    this.selectedTargetIds.set(current);
  }

  selectAll(): void {
    const allIds = new Set(
      this.uploadTargets.map((target) => target.target_id),
    );
    this.selectedTargetIds.set(allIds);
  }

  deselectAll(): void {
    this.selectedTargetIds.set(new Set());
  }

  get allSelected(): boolean {
    return (
      this.uploadTargets.length > 0 &&
      this.selectedTargetIds().size === this.uploadTargets.length
    );
  }

  onConfirm(): void {
    if (this.canConfirm) {
      this.confirm.emit(Array.from(this.selectedTargetIds()));
    }
  }

  onCancel(): void {
    this.cancel.emit();
  }

  private getTargetContext(
    target: CollectionWorkflowTargetDto,
  ): 'ecad' | 'streaming' | 'mechanical' | 'default' {
    const type = target.revenue_type?.toUpperCase();
    const subtype = target.revenue_subtype?.toUpperCase();

    // ECAD: Public performance + Execution
    if (type === 'PUBLIC_PERFORMANCE' && subtype === 'EXECUTION') {
      return 'ecad';
    }

    // Streaming revenue
    if (type === 'STREAMING') {
      return 'streaming';
    }

    // Mechanical rights
    if (type === 'MECHANICAL' || subtype === 'MECHANICAL') {
      return 'mechanical';
    }

    return 'default';
  }

  formatRevenueType(target: CollectionWorkflowTargetDto): string {
    const context = this.getTargetContext(target);
    return this.translocoService.translate(
      `share_target_type_${context}${this.integrationSuffix}`,
      {},
      'mzic-collection-flow',
    );
  }
}

